import { ProgramNames, PROVIDER_TYPES } from '../../../models'
import { PROGRAM_TAXONOMY, ProgramCustomerPropertyId } from '../../common/constants/customerProgram'
import { TREATMENT_OPTIONS } from '../../common/constants/treatmentOptions'
import { ConsentText, ProgramDisplayLabels } from '../messages'
import { ProgramConfig } from '../types'

export const COACHING_PROGRAM = {
  programName: ProgramNames.Coaching,
  blendedCareProgramId: '7ec82ca3-843f-4996-bdf9-49d8c7a35ed5',
  customerPropertyId: ProgramCustomerPropertyId.stressManagement,
  selfPayEligible: true,
  isCoachingProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.coaching,
    sessionSummary: 'coaching',
  },

  associatedProviderTypes: [PROVIDER_TYPES.COACH.lyraType],
  associatedProviderModalities: ['stressManagement'],

  canRescheduleAppointment: true,

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.COACHING],
    programTaxonomyTreatmentOptions: [
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.coaching,
        partner: PROGRAM_TAXONOMY.partner.blendedCare,
        offering: PROGRAM_TAXONOMY.offering.default,
      },
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.coaching,
        partner: PROGRAM_TAXONOMY.partner.blendedCare,
        offering: PROGRAM_TAXONOMY.offering.neurodiversitySkills,
      },
    ],
    isFullTreatmentOption: true,
  },

  consents: [
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement',
      showConsentIf: (options) => options?.isInternational === false,
    },
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement-2',
      showConsentIf: (options) => options?.isInternational === true,
    },
  ],

  questionIdentifiers: {
    SI_QUESTION: 'phq9_9',
    SI_PLAN_QUESTION: 'phq9_9_1',
    SI_INTENT_QUESTION: 'phq9_9_2',
  },

  noteContentMetadataName: {
    INITIAL_NOTE: 'bccInitialNote',
    SESSION_NOTE: 'bccSessionNote',
    GENERAL_NOTE: 'baseGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
    OLD_INITIAL_NOTE: 'coachingInitialNote',
    OLD_SESSION_NOTE: 'coachingSessionNote',
    OLD_GENERAL_NOTE: 'coachingGeneralNote',
    OLD_REFERRAL_NOTE: 'coachingReferralNote',
  },
} satisfies ProgramConfig

export const COACHING_FOR_PARENTS_PROGRAM = {
  programName: ProgramNames.CoachingForParents,
  blendedCareProgramId: '5ee0996e-c7cd-46d1-b009-462f6379cbd5',
  customerPropertyId: ProgramCustomerPropertyId.coachingForParents,
  isCoachingProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.coachingForParents,
    careType: 'Coaching',
    short: 'CFP',
    sessionSummary: 'coaching',
  },

  canRescheduleAppointment: true,
  selfPayEligible: true,

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.COACHING_FOR_PARENTS],
    programTaxonomyTreatmentOptions: [
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.coaching,
        partner: PROGRAM_TAXONOMY.partner.blendedCare,
        offering: PROGRAM_TAXONOMY.offering.parenting,
      },
    ],
    isFullTreatmentOption: true,
  },

  associatedProviderTypes: [PROVIDER_TYPES.COACH.lyraType],
  associatedProviderModalities: ['coachingForParents'],

  consents: [
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement',
      showConsentIf: (options) => options?.isInternational === false,
    },
    {
      consentText: ConsentText.coachingConsent,
      consentUrl: 'https://www.lyrahealth.com/bcc-agreement-2',
      showConsentIf: (options) => options?.isInternational === true,
    },
  ],

  questionIdentifiers: {
    SI_QUESTION: 'phq9_9',
    SI_PLAN_QUESTION: 'phq9_9_1',
    SI_INTENT_QUESTION: 'phq9_9_2',
  },

  noteContentMetadataName: {
    INITIAL_NOTE: 'bccInitialNote',
    SESSION_NOTE: 'bccSessionNote',
    GENERAL_NOTE: 'baseGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
  },
} satisfies ProgramConfig

export const SINGLE_SESSION_COACHING_PROGRAM = {
  programName: ProgramNames.SingleSessionCoaching,
  blendedCareProgramId: 'deb574e4-6aa3-4b47-bd21-d8be3ba5d42b',
  customerPropertyId: ProgramCustomerPropertyId.guidedSelfCareEnabled,
  isCoachingProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.guidedSelfCare,
    clientProfile: 'Guided self-care coaching',
    short: 'GSC',
    sessionSummary: 'coaching',
  },

  associatedProviderTypes: [PROVIDER_TYPES.COACH.lyraType],
  associatedProviderModalities: ['singleSessionCoaching'],

  canRescheduleAppointment: true,
  selfPayEligible: false,

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.GUIDED_SELF_CARE],
    programTaxonomyTreatmentOptions: [
      {
        clientele: PROGRAM_TAXONOMY.clientele.individual,
        treatment: PROGRAM_TAXONOMY.treatment.coaching,
        partner: PROGRAM_TAXONOMY.partner.blendedCare,
        offering: PROGRAM_TAXONOMY.offering.singleSession,
      },
    ],
    isFullTreatmentOption: true,
  },

  consents: [
    {
      consentText: ConsentText.guidedSelfCareConsent,
      consentUrl: 'https://www.lyrahealth.com/gsc-agreement',
      showConsentIf: () => true,
    },
  ],

  questionIdentifiers: {
    SI_QUESTION: 'phq9_9',
    SI_PLAN_QUESTION: 'phq9_9_1',
    SI_INTENT_QUESTION: 'phq9_9_2',
  },

  noteContentMetadataName: {
    INITIAL_NOTE: 'sscInitialNote',
    SESSION_NOTE: 'sscSessionNote',
    GENERAL_NOTE: 'baseGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
    OLD_INITIAL_NOTE: 'singleSessionCoachingInitialNote',
    OLD_SESSION_NOTE: 'singleSessionCoachingSessionNote',
    OLD_GENERAL_NOTE: 'singleSessionCoachingGeneralNote',
    OLD_REFERRAL_NOTE: 'singleSessionCoachingReferralNote',
  },
} satisfies ProgramConfig
